import { Div, Text } from "atomize"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import { DateTime } from "luxon"
import { useLocation, useNavigate } from "react-router-dom"
import { profileContext } from "./providers/profile.provider"
import { lessonsContext } from "./providers/lessons.provider"
import { isDesktopLike } from "./atoms/DesktopLikeView"
import { useProfileStats } from "./profile/useProfileStats"
import LessonHoursCard, { sumLessonHours } from "./components/LessonHoursCard"
import EarningsCard from "./components/EarningsCard"
import PaymentsCard from "./components/PaymentsCard"
import PlayersCard from "./components/PlayersCard"
import CourtsUsageCard from "./components/CourtsUsageCard"
import AbsencesCard from "./components/AbsencesCard"
import ProfileSlot, { expandOnArrival } from "./profile/ProfileSlot"
import ProfileReorder from "./profile/ProfileReorder"
import { DEFAULT_LAYOUTS, readLayout, sameLayout, writeLayout } from "./profile/profileLayout"
import AccountCard from "./components/AccountCard"
import AboutCard from "./components/AboutCard"
import AccountDataCard from "./components/AccountDataCard"
import FirstStepsCard from "./components/FirstStepsCard"
import OwnershipOfferCard from "./components/OwnershipOfferCard"
import GuideCard from "./components/GuideCard"
import NoSchoolCard from "./components/NoSchoolCard"
import SchoolCard from "./school/SchoolCard"
import Card from "./atoms/Card"
import EmptyState from "./atoms/EmptyState"
import { theme } from "./settings/config"
import {
	faChartSimple,
	faEuroSign,
	faFileInvoiceDollar,
	faLocationDot,
	faPersonRunning,
	faSliders,
	faTableTennisPaddleBall,
	faUser,
	faUserSlash,
	faUsers,
} from "@fortawesome/free-solid-svg-icons"
import CoachSchoolSettingsCard from "./school/CoachSchoolSettingsCard"
import ProfileCoachDropdown from "./profile/ProfileCoachDropdown"
import ProfileDateBar from "./profile/ProfileDateBar"
import { schoolContext } from "./providers/school.provider"
import { teachesInSchool } from "./utils/func.utils"
import { formatHoursAmount } from "./utils/week.utils"
import { isSmartphone } from "./atoms/SmartphoneView"
import PhonePageTitle, { usePhoneSummary } from "./header/PhonePageTitle"
import { PHONE_ISLAND_SIDE } from "./atoms/phoneBars"
import { sharedDataContext } from "./providers/sharedData.provider"
import useGestureHint, { HINTS } from "./week/useGestureHint"

// Il profilo su un telefono o su un PC: ognuno ha il suo ordine delle schede
const LAYOUT_KIND = isDesktopLike ? "pc" : "phone"
// sotto questa larghezza le due colonne del PC verrebbero strette: si va in
// una, prima la sinistra e poi la destra
const TWO_COLUMNS_MIN_WIDTH = 1024
// quanto si tiene ferma in cima la scheda a cui si arriva, mentre le altre
// si riempiono
const CARD_SETTLE_MS = 1500

const useWideEnough = () => {
	const [wide, setWide] = useState(() => window.innerWidth >= TWO_COLUMNS_MIN_WIDTH)
	useEffect(() => {
		const onResize = () => setWide(window.innerWidth >= TWO_COLUMNS_MIN_WIDTH)
		window.addEventListener("resize", onResize)
		return () => window.removeEventListener("resize", onResize)
	}, [])
	return wide
}

function ProfileContainer() {
	const { coach, logout } = useContext(profileContext)
	const { lessons } = useContext(lessonsContext)
	const { getSelectedSchool, selectedSchool } = useContext(schoolContext)
	const { getLessonType, getSport, getPaymentMethod } = useContext(sharedDataContext)

	const [layout, setLayout] = useState(() => readLayout(LAYOUT_KIND))
	const [isReordering, setIsReordering] = useState(false)
	// Tenere premuto non si vede: un avviso lo dice per le prime aperture del
	// profilo, e mai più dopo aver riordinato una volta
	const { showHint: showReorderHint, markUsed: markReorderUsed } = useGestureHint(
		HINTS.profileReorder,
		Boolean(coach && selectedSchool) && !isReordering,
	)
	// quanto era alta ogni scheda quando si è entrati: le righe partono da lì
	// e si chiudono, e con "Fatto" ci tornano prima di ridiventare schede
	const [cardHeights, setCardHeights] = useState(null)
	const [backFromReorder, setBackFromReorder] = useState(false)
	const firstStepsRef = useRef(null)
	const [hasFirstSteps, setHasFirstSteps] = useState(false)
	const isWide = useWideEnough()

	const changeLayout = useCallback(next => {
		setLayout(next)
		writeLayout(LAYOUT_KIND, next)
	}, [])

	const startReordering = () => {
		// i Primi passi, se ci sono ancora, si vedono nel riordino come
		// "resta in cima": se ci sono lo dice la loro scheda, non un conto
		setHasFirstSteps(Boolean(firstStepsRef.current?.firstElementChild))
		const heights = {}
		document.querySelectorAll("[data-profile-card]").forEach(element => {
			const height = element.getBoundingClientRect().height
			if (height > 0) heights[element.dataset.profileCard] = height
		})
		setCardHeights(heights)
		setBackFromReorder(false)
		markReorderUsed()
		setIsReordering(true)
	}

	const stopReordering = () => {
		setBackFromReorder(true)
		setIsReordering(false)
	}

	// Uscendo dal riordino anche i Primi passi si riaprono, invece di
	// comparire di colpo spingendo giù le schede che si stanno riaprendo
	const reduceMotion = useReducedMotion()
	useLayoutEffect(() => {
		const firstSteps = firstStepsRef.current
		if (isReordering || !backFromReorder || reduceMotion || !firstSteps?.firstElementChild) return
		return expandOnArrival(firstSteps, { fromRow: false })
		// solo quando si esce
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isReordering])

	const [summaryDate, setSummaryDate] = useState({
		start: DateTime.now().startOf("month"),
		end: DateTime.now().endOf("month"),
	})

	const {
		lessonsTypeCount,
		periodLessons,
		lessonsWithoutPaymentMethod,
		courtsUsage,
		lessonsPaymentAmountByMethod,
		playersLessonCount,
		earnings,
		selectedTimeSlot,
		setSelectedTimeSlot,
	} = useProfileStats(summaryDate)

	const hasStats = Boolean(lessons?.[selectedSchool] && coach)
	const school = getSelectedSchool()
	// Chi qui non insegna non ha numeri suoi né condizioni sue: vede i
	// riepiloghi dei maestri che il suo ruolo gli fa guardare, dal selettore
	const teachesHere = teachesInSchool(coach, selectedSchool)

	const greeting = `Ciao ${coach?.coachName?.split(" ")[0] ?? ""}!`
	// nella pillola in cima al telefono: le ore del periodo che si guarda,
	// le stesse della scheda "Ore di lezione". Chi qui non insegna non ne ha
	const totalHours = sumLessonHours(lessonsTypeCount)
	// (finché le conta, undefined: la pillola tiene il posto della riga)
	usePhoneSummary(
		!hasStats || (teachesHere && lessonsTypeCount == null)
			? undefined
			: teachesHere && totalHours > 0
				? `${formatHoursAmount(totalHours)} di lezione`
				: null,
	)

	// Dal tondo del circolo in cima al telefono si arriva qui per la scheda
	// del circolo: la pagina si apre già lì. Le schede sopra si riempiono un
	// attimo dopo e la spingerebbero giù, quindi per poco la si tiene ferma
	// in cima mentre la pagina cresce — finché il dito non tocca. Poi la
	// richiesta si toglie, se no tornando indietro si riscenderebbe.
	const location = useLocation()
	const navigate = useNavigate()
	const cardToShow = location.state?.card
	useEffect(() => {
		if (!cardToShow) return
		const rem = parseFloat(window.getComputedStyle(document.documentElement).fontSize)
		const align = () => {
			const card = document.querySelector(`[data-profile-card="${cardToShow}"]`)
			if (!card) return
			const header = document.querySelector("[data-phone-header]")
			const top =
				card.getBoundingClientRect().top +
				window.scrollY -
				(header?.getBoundingClientRect().bottom ?? 0) -
				rem
			window.scrollTo({ top: Math.max(0, top) })
		}
		align()
		const observer = new ResizeObserver(align)
		observer.observe(document.body)
		const release = () => {
			observer.disconnect()
			clearTimeout(timer)
			window.removeEventListener("pointerdown", stop)
			window.removeEventListener("wheel", stop)
		}
		// chi se ne va prima dal profilo non ci deve essere riportato: lì si
		// lascia solo la presa, e la richiesta resta dov'era
		const stop = () => {
			release()
			navigate(location.pathname, { replace: true, state: null })
		}
		const timer = setTimeout(stop, CARD_SETTLE_MS)
		window.addEventListener("pointerdown", stop)
		window.addEventListener("wheel", stop, { passive: true })
		return release
		// solo quando arriva una richiesta
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cardToShow])

	// cosa c'è dentro ogni posto, e come si chiama quando è chiuso nel riordino
	const cards = {
		hours: {
			title: "Ore di lezione",
			icon: faPersonRunning,
			color: theme.colors.primary,
			bg: theme.colors.primary_light,
			render: () =>
				hasStats ? (
					<LessonHoursCard
						lessonsTypeCount={lessonsTypeCount}
						periodLessons={periodLessons}
						summaryDate={summaryDate}
						getLessonType={getLessonType}
						getSport={getSport}
					/>
				) : (
					coach &&
					teachesHere && (
						<Card bg={theme.colors.surface}>
							<EmptyState
								icon={faChartSimple}
								title="I tuoi numeri, appena ci saranno"
								text="Ore, presenze, incassi e costo dei campi si calcolano da soli dalle lezioni che segni. Segnane una e questa scheda si riempie."
							/>
						</Card>
					)
				),
		},
		earnings: {
			title: "Compensi",
			icon: faFileInvoiceDollar,
			color: theme.colors.yellow,
			bg: theme.colors.yellow_light,
			render: () =>
				hasStats && <EarningsCard earnings={earnings} getLessonType={getLessonType} />,
		},
		payments: {
			title: "Pagamenti",
			icon: faEuroSign,
			color: theme.colors.green,
			bg: theme.colors.green_light,
			render: () =>
				hasStats && (
					<PaymentsCard
						lessonsPaymentAmountByMethod={lessonsPaymentAmountByMethod}
						getPaymentMethod={getPaymentMethod}
						lessonsWithoutPaymentMethod={lessonsWithoutPaymentMethod}
						summaryDate={summaryDate}
					/>
				),
		},
		players: {
			title: "Allievi",
			icon: faUsers,
			color: theme.colors.blue,
			bg: theme.colors.blue_light,
			render: () => hasStats && <PlayersCard playersLessonCount={playersLessonCount} />,
		},
		courts: {
			title: "Utilizzo campi",
			icon: faTableTennisPaddleBall,
			color: theme.colors.gunmetal,
			bg: theme.colors.gunmetal_lighter,
			render: () =>
				hasStats && (
					<CourtsUsageCard
						courtsUsage={courtsUsage}
						selectedTimeSlot={selectedTimeSlot}
						courtTimeSlots={school?.courtTimeSlots}
						setSelectedTimeSlot={setSelectedTimeSlot}
						getSport={getSport}
						getSelectedSchool={getSelectedSchool}
					/>
				),
		},
		absences: {
			title: "Assenze abbonati",
			icon: faUserSlash,
			color: theme.colors.red,
			bg: theme.colors.red_light,
			render: () => hasStats && <AbsencesCard />,
		},
		school: {
			title: school?.schoolName || "Il circolo",
			icon: faLocationDot,
			color: "white",
			bg: theme.colors.gunmetal_fill,
			render: () => <SchoolCard />,
		},
		conditions: {
			title: "Le tue condizioni",
			icon: faSliders,
			color: "white",
			bg: theme.colors.accent,
			render: () => teachesHere && <CoachSchoolSettingsCard />,
		},
		account: {
			title: coach?.coachName || "Il tuo account",
			icon: faUser,
			color: "white",
			bg: theme.colors.gunmetal_fill,
			render: () => (
				<AccountCard
					schoolName={school?.schoolName}
					coachID={coach?.coachID}
					onLogout={() => logout()}
				/>
			),
		},
	}

	// senza un circolo le schede da spostare sono quasi tutte vuote
	const canReorder = Boolean(coach && selectedSchool)
	const slot = id => (
		<ProfileSlot
			key={id}
			cardID={id}
			appear={backFromReorder}
			onLongPress={canReorder ? startReordering : null}>
			{cards[id].render()}
		</ProfileSlot>
	)

	// Fissi in fondo allo schermo, fuori dal riordino: la barra delle date
	// scende via quando si entra, e torna su quando si esce
	const floating = (
		<AnimatePresence>
			{!isReordering && isDesktopLike && <ProfileCoachDropdown key="coach" />}
			{!isReordering && (
				<ProfileDateBar
					key="dates"
					summaryDate={summaryDate}
					setSummaryDate={setSummaryDate}
					hint={showReorderHint && "Tieni premuto il titolo di una scheda per spostarla"}
				/>
			)}
		</AnimatePresence>
	)

	return (
		<>
			{/* sul telefono il saluto sta a sinistra, sulla riga della pillola
			    in cima; il suo posto resta anche quando non c'è (nel riordino),
			    se no la pagina finirebbe sotto la pillola */}
			{isSmartphone && (
				<PhonePageTitle textSize="title" textWeight="400" side="0px" inset={PHONE_ISLAND_SIDE}>
					{!isReordering && coach?.coachName && (
						<motion.span
							initial={backFromReorder ? { opacity: 0 } : false}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.3 }}>
							{greeting}
						</motion.span>
					)}
				</PhonePageTitle>
			)}
			{!isSmartphone && !isReordering && coach?.coachName && (
				<motion.div
					initial={backFromReorder ? { opacity: 0 } : false}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.3 }}>
					<Text textSize="title" textAlign="center" m={{ t: "1rem" }}>
						{greeting}
					</Text>
				</motion.div>
			)}

			{!isReordering && coach && !selectedSchool && <NoSchoolCard />}

			{/* in cima sempre: sparisce da sola quando i passi sono fatti. Nel
			    riordino resta montata, nascosta: rifacendosi da capo saprebbe
			    se c'è solo dopo un attimo, e uscendo spingerebbe giù le schede */}
			<div ref={firstStepsRef} style={isReordering ? { display: "none" } : undefined}>
				{/* un circolo che qualcuno ti passa viene prima di tutto */}
				<OwnershipOfferCard />
				<FirstStepsCard />
			</div>

			{isReordering ? (
				<ProfileReorder
					layout={layout}
					cards={cards}
					onChange={changeLayout}
					canReset={!sameLayout(layout, DEFAULT_LAYOUTS[LAYOUT_KIND])}
					onReset={() => changeLayout(DEFAULT_LAYOUTS[LAYOUT_KIND])}
					onDone={stopReordering}
					hasFirstSteps={hasFirstSteps}
					stacked={!isWide}
					cardHeights={cardHeights}
				/>
			) : (
				<>
					{/* Le schede che si spostano. Sul PC largo stanno in due
					    colonne che scendono ognuna per conto suo; i margini delle
					    schede (1rem per parte) farebbero in mezzo il doppio dei
					    bordi, e le colonne si stringono di mezzo rem verso il
					    centro. */}
					{layout.length > 1 && isWide ? (
						<Div d="flex" align="flex-start">
							{layout.map((ids, c) => (
								<Div
									key={c}
									flexGrow="1"
									style={{ flexBasis: 0, minWidth: 0 }}
									m={c === 0 ? { r: "-0.5rem" } : { l: "-0.5rem" }}>
									{ids.map(slot)}
								</Div>
							))}
						</Div>
					) : (
						layout.flat().map(slot)
					)}

					{/* la guida si cerca quando serve, non si guarda ogni giorno:
					    sta in fondo, sotto le schede che si spostano, e non spinge
					    giù i numeri */}
					{coach && <GuideCard />}

					<AccountDataCard />

					<AboutCard />
				</>
			)}

			{floating}
		</>
	)
}

export default ProfileContainer
